import { captureException } from "@sentry/react";
import i18n from "i18next";
import { useSetAtom } from "jotai";
import { useEffect } from "react";
import { setupClient } from "web_myplann/Infrastructure/Api/Api";
import { setupClient as setupClientOld } from "web_myplann/Infrastructure/ApiOld/Api";
import { setBridgedOnlineStatus } from "web_myplann/Infrastructure/Hooks/onlineStatusStore";
import { useColorScheme } from "web_myplann/Infrastructure/Themes/ColorSchemeProvider";
import { apiInitializedAtom } from "web_myplann/Shared/State/apiInitializedAtom";
import {
	appInfoState,
	biometryEnabledState,
} from "web_myplann/Shared/State/appInfoAtom";
import { rightsState } from "web_myplann/Shared/State/rightsAtom";
import { sessionState } from "web_myplann/Shared/State/sessionAtom";
import { createAuthorizationHeaders } from "web_myplann/Shared/Utils/createAuthorizationHeaders";
import { device } from "web_myplann/Utils/device";
import {
	type InitResponse,
	type WebViewMessage,
	WebViewMessageTypes,
} from "web_myplann/Utils/WebViewModel";

const INIT_BURST_ATTEMPTS = 5;
const INIT_RETRY_BASE_DELAY_MS = 500;

const delay = (ms: number) =>
	new Promise<void>((resolve) => {
		setTimeout(resolve, ms);
	});

const isBridgeReadyMessage = (data: unknown) =>
	typeof data === "object" &&
	data !== null &&
	(data as WebViewMessage).type === WebViewMessageTypes.BRIDGE_READY;

export const useInitializeWeb = () => {
	const { setMode } = useColorScheme();
	const setApiInitialized = useSetAtom(apiInitializedAtom);
	const setSessionState = useSetAtom(sessionState);
	const setAppInfoState = useSetAtom(appInfoState);
	const setBiometryEnabled = useSetAtom(biometryEnabledState);
	const setRights = useSetAtom(rightsState);

	useEffect(() => {
		let cancelled = false;
		let initialized = false;
		let running = false;
		let retryRequested = false;
		let burst = 0;

		const applyInitResponse = (payload: InitResponse["payload"]) => {
			const authMiddleware = {
				name: "Add JWT token",
				fn: (request: any) => {
					request.options.headers = createAuthorizationHeaders(
						{ token: payload.bearer, tokenVerify: payload.tokenVerify },
						request.options.headers,
					);
					return request;
				},
			};
			const clientConfig = {
				apiUrl: import.meta.env.VITE_MYPLANN_URL,
				jwtKey: () => `Bearer ${payload.bearer}`,
				requestMiddlewares: [authMiddleware],
			};

			setupClient(clientConfig);
			setupClientOld(clientConfig);
			// BACKWARD COMPAT (DEV-3888): native shells older than the release that
			// added `rights` to INIT_RESPONSE send none; RightsProvider then fetches
			// them itself. Once Remote Config `min_version` is at or above that
			// release, make this unconditional and drop the fallback in RightsProvider.
			if (payload.rights) {
				setRights(payload.rights);
			}
			setApiInitialized(true);
			setSessionState({
				isAuthenticated: true,
				token: payload.bearer,
				tokenVerify: payload.tokenVerify,
			});
			i18n.changeLanguage(payload.language);

			setMode({ paletteMode: payload.darkMode ? "dark" : "light" });
			setAppInfoState({ appVersion: payload.appVersion });
			setBiometryEnabled(payload.biometryEnabled ?? false);
			setBridgedOnlineStatus(payload.isOnline ?? true);
		};

		async function initializeWeb() {
			if (cancelled || initialized) {
				return;
			}

			if (running) {
				retryRequested = true;
				return;
			}

			running = true;
			burst += 1;

			try {
				for (
					let attempt = 1;
					attempt <= INIT_BURST_ATTEMPTS && !cancelled;
					attempt++
				) {
					try {
						const response = await device.initializeWeb();

						if (cancelled) {
							return;
						}

						applyInitResponse(response.payload);
						initialized = true;

						return;
					} catch (error) {
						if (cancelled) {
							return;
						}

						if (attempt === INIT_BURST_ATTEMPTS) {
							captureException(error, (scope) => {
								scope.setExtra("initBurst", burst);
								return scope;
							});

							return;
						}

						await delay(INIT_RETRY_BASE_DELAY_MS * 2 ** (attempt - 1));
					}
				}
			} finally {
				running = false;

				if (retryRequested && !initialized && !cancelled) {
					retryRequested = false;
					void initializeWeb();
				}
			}
		}

		const handleBridgeReady = (event: MessageEvent) => {
			if (isBridgeReadyMessage(event.data)) {
				void initializeWeb();
			}
		};

		window.addEventListener("message", handleBridgeReady);
		initializeWeb();

		return () => {
			cancelled = true;
			window.removeEventListener("message", handleBridgeReady);
		};
	}, []);
};
